import { createContext, useContext, useState, useCallback } from 'react';
import { ExternalLink as ExternalLinkIcon, ArrowUpRight, X } from 'lucide-react';
import { useT } from '../i18n/LanguageContext';

const Ctx = createContext(null);

export function ExternalLinkProvider({ children }) {
  const [pending, setPending] = useState(null); // { url, label }
  const { t } = useT();

  const confirm = useCallback((url, label = '') => {
    if (!url || url === '#') return;
    setPending({ url, label });
  }, []);

  const go = () => {
    if (pending?.url) window.open(pending.url, '_blank', 'noopener,noreferrer');
    setPending(null);
  };
  const stay = () => setPending(null);

  return (
    <Ctx.Provider value={{ confirm }}>
      {children}
      {pending && (
        <div
          data-testid="external-link-modal"
          className="fixed inset-0 z-[300] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
          onClick={stay}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="surface border border-subtle rounded-lg max-w-md w-full p-8 shimmer-border"
          >
            <div className="relative">
              <button
                onClick={stay}
                aria-label="Close"
                className="absolute -top-2 -right-2 text-muted hover:text-prim p-1"
              >
                <X size={18} />
              </button>
              <div className="w-12 h-12 rounded-full bg-brand-teal/10 border border-brand-teal/30 flex items-center justify-center mb-5">
                <ExternalLinkIcon size={20} className="text-brand-teal-light" />
              </div>
              <p className="eyebrow mb-3">— {t('externalLink.eyebrow') || 'You\'re leaving MBK'}</p>
              <h3 className="font-display text-2xl text-prim tracking-tight leading-snug">
                {t('externalLink.title') || 'This will take you to another website.'}
              </h3>
              {pending.label && (
                <p className="mt-3 text-sec text-sm">
                  {t('externalLink.destination') || 'Destination'}: <span className="text-brand-teal-light font-display">{pending.label}</span>
                </p>
              )}
              <p className="mt-4 text-sec text-sm leading-relaxed break-all font-mono">
                {pending.url}
              </p>
              <div className="mt-8 flex flex-wrap gap-3 justify-end">
                <button
                  onClick={stay}
                  data-testid="external-link-no"
                  className="btn-secondary text-sm"
                >
                  {t('externalLink.no') || 'Stay here'}
                </button>
                <button
                  onClick={go}
                  data-testid="external-link-yes"
                  className="btn-primary text-sm"
                >
                  {t('externalLink.yes') || 'Continue'} <ArrowUpRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}

export function useExternalLink() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useExternalLink must be inside ExternalLinkProvider');
  return ctx;
}

// Convenience: drop-in <ExtLink href="..." label="..."> for anchors that should be confirmed
export function ExtLink({ href, label = '', children, className = '', dataTestid }) {
  const { confirm } = useExternalLink();
  const onClick = (e) => {
    if (!href || href === '#') return;
    e.preventDefault();
    confirm(href, label);
  };
  return (
    <a href={href || '#'} onClick={onClick} className={className} data-testid={dataTestid}>
      {children}
    </a>
  );
}
